import { Card, EmptyState, Eyebrow } from '@/components/ui';
import { InsightCard } from './InsightCard';
import { useSkipIntelligence } from '@/features/ai/useSkipIntelligence';
import { useTodayAgenda } from '@/components/today/useTodayAgenda';
import { NowStack } from '@/components/today/NowStack';
import { UpNextList } from '@/components/today/UpNextList';

function RailHeading({ children }: { children: string }) {
  return (
    <Eyebrow as="h2" className="mb-2">
      {children}
    </Eyebrow>
  );
}

/**
 * The desktop "Narrator" rail (xl+). Mirrors the mobile Today agenda — the same
 * "Right now" hero card and "Up next" list, driven by the shared useTodayAgenda
 * hook — so the desktop shell reflects the live day instead of the dead
 * "No active task" placeholders it used to show. Closes the mobile↔desktop gap.
 */
export function Narrator() {
  const { now, isViewingToday, nowItems, defaultNowKey, upNext } = useTodayAgenda(5);
  const insight = useSkipIntelligence();

  return (
    <aside
      aria-label="Narrator"
      className="hidden h-full w-[320px] flex-col gap-5 overflow-y-auto border-l border-line bg-canvas px-4 py-5 xl:flex"
    >
      <section>
        <RailHeading>Right now</RailHeading>
        {nowItems.length > 0 ? (
          <NowStack items={nowItems} defaultKey={defaultNowKey} now={now} />
        ) : (
          <EmptyState
            align="start"
            title={isViewingToday ? 'Nothing happening right now.' : 'Open day'}
            move="Ask Brainday below — or capture a task in the sidebar."
          />
        )}
      </section>

      <section>
        <RailHeading>Up next</RailHeading>
        {upNext.length > 0 ? (
          <UpNextList items={upNext} now={now} inset={false} />
        ) : (
          <EmptyState
            align="start"
            tone="quiet"
            title="Nothing else scheduled."
            move="Capture in the sidebar to plan ahead."
          />
        )}
      </section>

      <section>
        <RailHeading>Insight</RailHeading>
        {insight ? (
          <InsightCard insight={insight} />
        ) : (
          <Card>
            <div className="text-caption leading-snug text-t3">
              Insights appear here once you&apos;ve completed a few tasks.
            </div>
          </Card>
        )}
      </section>
    </aside>
  );
}
